import type IntegrationTypes from "website_script/constants/IntegrationTypes"
import type ModuleTypes from "website_script/constants/ModuleTypes"
import type { AbTestingStoreType } from "website_script/stores/abTesting"
import type { Settings } from "website_script/types/settings"
import getGlobalWebsiteData from "./getGlobalWebsiteData"
import type { ElementData } from "./Parsers"
import { FlowType } from "conversion_flows/types/scalars"

export type BuildUrlDataInput = {
  settings: Settings
  abTesting: AbTestingStoreType
  moduleType: ModuleTypes
  integrationType?: IntegrationTypes
  conversionFlow?: FlowType
  departmentUuid?: string | null
  leadFlowId?: number
  elData?: ElementData
  leadId?: string | null
  leadToken?: string | null
}

export type BuildUrlDataOutput = {
  refUrl: string
  cId: string
  depId?: string | null
  leadFlowId?: number
  host: string
  d: string
  moduleType: ModuleTypes
  au_car_id?: string
  data_from_source: string
  conversion_flow?: FlowType
  bm_text?: string
  hide_au_logo?: number
  vin_lpn?: string
  lead_id: string | null
  lead_token: string | null
  [key: `exp[${string}]`]: string | undefined
}

export const buildUrlData = ({
  settings,
  abTesting,
  moduleType,
  integrationType,
  conversionFlow,
  departmentUuid,
  leadFlowId,
  elData,
  leadId,
  leadToken,
}: BuildUrlDataInput): BuildUrlDataOutput => {
  const { iframeHost, id } = settings
  const { sellerUrl, scriptData, customData, stockId, auCarId, departmentId } =
    getGlobalWebsiteData()

  // In case elData is not passed to the function
  // In order to call ex. elData.carUrl, elData cannot be undefined, but should be empty object
  const elementData = elData

  const d = { ...scriptData }

  // depId gets assigned in order:
  // 1. data-department-id present for the widget
  // 2. departmentId present for global object AuEmbed.config after our script loads
  // 3. departmentUuid assigned when our script loads
  // 4. depId coming from scriptData
  const depId =
    departmentUuid || departmentId || settings.departmentUuid || d.depId
  d.depId = depId

  const urlBuiltData: BuildUrlDataOutput = {
    // elementData.carUrl for similar cars widget,
    // elementData.sellerUrl for other modules
    // sellerUrl as a fallback for both, defaults to window.location.href
    refUrl: elementData?.carUrl || elementData?.sellerUrl || sellerUrl,
    cId: id,
    depId: depId,
    leadFlowId,
    host: iframeHost,
    d: JSON.stringify(d),
    moduleType: moduleType,
    au_car_id: auCarId,
    data_from_source: JSON.stringify({
      customData: elementData?.customData ?? customData,
      stockId: elementData?.stockId || stockId,
      integrationType: integrationType,
      ...(elementData?.integrationId
        ? { integrationId: elementData?.integrationId }
        : {}),
    }),
    conversion_flow: conversionFlow,
    bm_text: settings.initialBrand ? settings.initialBrand : "",
    lead_id: leadId ?? null,
    lead_token: leadToken ?? null,
  }

  // Add the `exp` nested object to the params, but flatten it first
  if (abTesting.runningExperiments) {
    Object.entries(abTesting.runningExperiments).forEach(
      ([expName, version]) => {
        urlBuiltData[`exp[${expName}]`] = version
      }
    )
  }

  if (elementData?.hasExternalLogo) {
    urlBuiltData.hide_au_logo = 1
  }

  return urlBuiltData
}
